import React, { useRef, useState, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import messages from '../../assets/Messages.json';
import axios from 'axios';
import { API_URL } from '../../Constant';
import { showLoading } from 'react-global-loading';
import { getAuthHeaders, hasAuthSession } from '../../utils/authStorage';

// Helper function to mark a page as saved (green in header)
// Call this after successful save in any balance sheet page
export const markPageAsSaved = (pageName) => {
  try {
    const savedPages = JSON.parse(localStorage.getItem('balanceSheetSavedPages') || '[]');
    if (!savedPages.includes(pageName)) {
      savedPages.push(pageName);
      localStorage.setItem('balanceSheetSavedPages', JSON.stringify(savedPages));
    }
    return true;
  } catch (error) {
    console.error('Error updating saved pages:', error);
    return false;
  }
};

// Helper function to clear all saved pages (use on logout or reset / DSL erase)
export const clearSavedPages = () => {
  try {
    localStorage.removeItem('balanceSheetSavedPages');
    localStorage.removeItem('balanceSheetAccessPageIds');
    localStorage.removeItem('balanceSheetApiCalled');
    localStorage.removeItem('balanceSheetDslYear');
    localStorage.removeItem('balanceSheetDataSaved');
    return true;
  } catch (error) {
    console.error('Error clearing saved pages:', error);
    return false;
  }
};

// Helper function to get auth config (for use in refreshBalanceSheetValidation)
const getAuthConfig = () => {
  if (!hasAuthSession()) {
    return null;
  }
  const { headers } = getAuthHeaders();
  return {
    headers: {
      ...headers,
      'Cache-Control': 'no-cache',
    },
  };
};

// Helper function to refresh balance sheet validation
// Call this after successful save in any balance sheet page
export const refreshBalanceSheetValidation = async (dslNo, surveyYear) => {
  try {
    const currentConfig = getAuthConfig();
    if (!currentConfig) {
      console.error('Authentication token not found for balance sheet validation');
      return false;
    }

    // Extract only year part (e.g., "2024" from "2024-25")
    const yearOnly = surveyYear ? String(surveyYear).split('-')[0] : surveyYear;

    const payload = {
      dsl_no: dslNo,
      survey_year: yearOnly
    };

    const response = await axios.post(`${API_URL}api/identification-particulars/blockValidationbalanceSheet`, payload, currentConfig);

    if (response.data && response.data.status) {
      const { data } = response.data;
      // API returns allblockData array of page IDs
      const accessIds = data.allblockData || [];

      if (Array.isArray(accessIds) && accessIds.length > 0) {
        const numericIds = accessIds.map(id => Number(id));
        localStorage.setItem('balanceSheetAccessPageIds', JSON.stringify(numericIds));
        localStorage.setItem('balanceSheetApiCalled', 'true'); // Mark API as called
        // Trigger a custom event to notify BalanceSheetHeader to update
        window.dispatchEvent(new CustomEvent('balanceSheetValidationUpdated', { detail: numericIds }));
        return true;
      } else {
        // If allblockData is empty, set only first page accessible
        localStorage.setItem('balanceSheetAccessPageIds', JSON.stringify([1]));
        localStorage.setItem('balanceSheetApiCalled', 'true'); // Mark API as called
        // Clear flags that enable all pages
        localStorage.removeItem('balanceSheetDataLoaded');
        localStorage.removeItem('balanceSheetDataSaved');
        // Trigger event with only first page
        window.dispatchEvent(new CustomEvent('balanceSheetValidationUpdated', { detail: [1] }));
        return true;
      }
    }
    // If API returns false status, clear API called flag
    localStorage.removeItem('balanceSheetApiCalled');
    localStorage.setItem('balanceSheetAccessPageIds', JSON.stringify([1]));
    window.dispatchEvent(new CustomEvent('balanceSheetValidationUpdated', { detail: [1] }));
    return false;
  } catch (error) {
    console.error('Error refreshing balance sheet validation:', error);
    // On error, clear API called flag
    localStorage.removeItem('balanceSheetApiCalled');
    localStorage.setItem('balanceSheetAccessPageIds', JSON.stringify([1]));
    window.dispatchEvent(new CustomEvent('balanceSheetValidationUpdated', { detail: [1] }));
    return false;
  }
};

// Page names constants for easy reference
export const PAGE_NAMES = {
  GENERAL_INFORMATION: 'GeneralInformation',
  FIXED_ASSETS: 'FixedAssets',
  BALANCE_SHEET_DATA: 'BalanceSheetData',
  PROFIT_AND_LOSS: 'ProfitAndLossAccount',
  EMPLOYMENT_LABOUR_COST: 'EmploymentAndLabourCost',
  INPUT_ITEMS_INDIGENOUS: 'InputItemsIndigenous',
  INPUT_ITEMS_IMPORTED: 'InputItemsImported',
  PRODUCTS_BY_PRODUCTS: 'ProductsAndByProducts',
  ICT_ENERGY: 'ICTUsageAndEnergyConservation',
  FIELD_OPERATIONS: 'ParticularsOfFieldOperations',
  PART_A: 'PartA',
  PART_2: 'Part2',
  NET_PROFIT_TALLY: 'NetProfitTally',
  FINAL_CHECKLIST: 'FinalCheckList',
  VALIDATION: 'Validation',
  REPORTS: 'Reports'
};

const balanceSheetPages = [
  { id: 1, name: 'GeneralInformation', title: 'Block A-B General Information', route: '/balanceSheet' },
  { id: 2, name: 'FixedAssets', title: 'Block C Fixed Assets Data', route: '/fixedAssets' },
  { id: 3, name: 'BalanceSheetData', title: 'Balance Sheet', route: '/balanceSheetData' },
  { id: 4, name: 'ProfitAndLossAccount', title: 'Profit and Loss Account', route: '/profitAndLossAccount' },
  { id: 5, name: 'EmploymentAndLabourCost', title: 'Block E', route: '/employmentAndLabourCost' },
  { id: 6, name: 'InputItemsIndigenous', title: 'Block H: Input Items', route: '/input-items' },
  { id: 7, name: 'InputItemsImported', title: 'Block I: Input Items', route: '/input-items-imported' },
  { id: 8, name: 'ProductsAndByProducts', title: 'Block J: Manufactured by unit', route: '/products-and-by-products' },
  { id: 9, name: 'ICTUsageAndEnergyConservation', title: 'Block KL: IT/Energy Conservation', route: '/ict-usage-energy-conservation' },
  { id: 10, name: 'PartA', title: 'Part A-1', route: '/part-a' },
  // { id: 11, name: 'NetProfitTally', title: 'Net Profit Tally', route: '/net-profit-tally' },
  { id: 11, name: 'FinalCheckList', title: 'Final Checklist', route: '/final-checklist' },
  { id: 12, name: 'NetProfitTally', title: 'Net Profit Tally', route: '/net-profit-tally' },

  // { id: 10, name: 'ParticularsOfFieldOperations', title: 'Particulars of Field Operations', route: '/particulars-of-field-operations' },
  // { id: 12, name: 'Part2', title: 'Part-2', route: '/part2' },
  // { id: 14, name: 'Validation', title: 'Validation', route: '/validation' },
  // { id: 15, name: 'Reports', title: 'Reports', route: '/report-selection' },
];

export default function BalanceSheetHeader({ pageInfo }) {
  const location = useLocation();
  const toastShownRef = useRef({}); // Track which toast messages have been shown
  const [accessPageIds, setAccessPageIds] = useState([1]); // Store accessible page IDs from API - default to only first page
  const [apiCalled, setApiCalled] = useState(false); // Track if API has been called successfully
  const blockValidationRef = useRef({ isFetching: false, lastPayload: null });

  const getAuthConfigLocal = () => {
    if (!hasAuthSession()) {
      console.error('No active auth session');
      return null;
    }
    const { headers } = getAuthHeaders();
    return {
      headers: {
        ...headers,
        'Cache-Control': 'no-cache',
      },
    };
  };

  // Balance Sheet Validation API call - similar to blockValidation
  const balanceSheetValidation = useCallback(async (dslNo, surveyYear) => {
    if (!dslNo || !surveyYear) {
      console.log('DSL No or Survey Year not available for balance sheet validation');
      return;
    }

    // Create a unique key for the payload
    const postData = {
      dsl_no: dslNo,
      survey_year: surveyYear
    };
    const payloadKey = JSON.stringify(postData);

    // Check if same payload is already being fetched
    if (blockValidationRef.current.isFetching && blockValidationRef.current.lastPayload === payloadKey) {
      return;
    }

    // Check if same payload was just fetched
    if (blockValidationRef.current.lastPayload === payloadKey && !blockValidationRef.current.isFetching) {
      return;
    }

    try {
      blockValidationRef.current.isFetching = true;
      blockValidationRef.current.lastPayload = payloadKey;

      const currentConfig = getAuthConfigLocal();
      if (!currentConfig) {
        console.error('Authentication token not found for balance sheet validation');
        blockValidationRef.current.isFetching = false;
        return;
      }

      // Extract only year part (e.g., "2024" from "2024-25")
      const yearOnly = surveyYear ? String(surveyYear).split('-')[0] : surveyYear;

      const payload = {
        dsl_no: dslNo,
        survey_year: yearOnly
      };

      const response = await axios.post(`${API_URL}api/identification-particulars/blockValidationbalanceSheet`, payload, currentConfig);

      if (response.data && response.data.status) {
        const { data } = response.data;
        // API returns allblockData array of page IDs
        const accessIds = data.allblockData || [];

        // Mark API as called successfully
        setApiCalled(true);

        if (Array.isArray(accessIds) && accessIds.length > 0) {
          // Convert to numbers if they're strings
          const numericIds = accessIds.map(id => Number(id));
          setAccessPageIds(numericIds);
          // Store in localStorage for persistence
          localStorage.setItem('balanceSheetAccessPageIds', JSON.stringify(numericIds));
          localStorage.setItem('balanceSheetApiCalled', 'true'); // Mark that API was called
        } else {
          // If allblockData is empty, set only first page accessible
          setAccessPageIds([1]);
          localStorage.setItem('balanceSheetAccessPageIds', JSON.stringify([1]));
          localStorage.setItem('balanceSheetApiCalled', 'true'); // Mark that API was called
          // Clear flags that enable all pages when no data is available
          localStorage.removeItem('balanceSheetDataLoaded');
          localStorage.removeItem('balanceSheetDataSaved');
        }
      } else {
        // If API returns false status, set default accessible pages
        setAccessPageIds([1]);
        localStorage.setItem('balanceSheetAccessPageIds', JSON.stringify([1]));
        localStorage.setItem('balanceSheetApiCalled', 'true'); // Mark that API was called
      }
    } catch (error) {
      console.error('Error fetching balance sheet validation:', error);
      // On error, set only first page accessible (grey for others)
      setAccessPageIds([1]);
      setApiCalled(false); // Mark API as not called successfully
      localStorage.setItem('balanceSheetAccessPageIds', JSON.stringify([1]));
      localStorage.removeItem('balanceSheetApiCalled'); // Clear API called flag
    } finally {
      blockValidationRef.current.isFetching = false;
    }
  }, []);

  // Normalize dsl_no and survey_year from storage (hard refresh / any load) so we never call APIs with empty payload
  const getDslAndYearFromStorage = (parsedData) => {
    const rawDsl = parsedData.dslNo ?? parsedData.dsl_no ?? '';
    const rawYear = parsedData.year ?? parsedData.surveyYear ?? parsedData.survey_year ?? '';
    const dslNo = String(rawDsl).trim();
    let surveyYear = rawYear ? String(rawYear).trim() : '';
    if (surveyYear && /^\d{4}$/.test(surveyYear)) {
      surveyYear = `${surveyYear}-${(parseInt(surveyYear, 10) + 1).toString().slice(-2)}`;
    }
    return { dslNo, surveyYear };
  };

  // Fetch validation data on component mount (hard refresh / navigate – dono APIs ko sahi payload ke sath call karo)
  useEffect(() => {
    const balanceSheetData = localStorage.getItem('balanceSheetFormData');
    const apiCalledFlag = localStorage.getItem('balanceSheetApiCalled') === 'true';

    if (balanceSheetData) {
      try {
        const parsedData = JSON.parse(balanceSheetData);
        const { dslNo, surveyYear } = getDslAndYearFromStorage(parsedData);

        if (dslNo && surveyYear) {
          // After Block A-B save, allow navigation to any page (2–11): use stored IDs so API does not overwrite
          const dataSavedFlag = localStorage.getItem('balanceSheetDataSaved') === 'true';
          if (dataSavedFlag) {
            const storedIds = localStorage.getItem('balanceSheetAccessPageIds');
            if (storedIds) {
              try {
                const parsedIds = JSON.parse(storedIds);
                if (Array.isArray(parsedIds) && parsedIds.length > 0) {
                  setAccessPageIds(parsedIds);
                  setApiCalled(true);
                  return;
                }
              } catch (e) { /* fall through to API */ }
            }
          }
          balanceSheetValidation(dslNo, surveyYear);
        } else {
          if (apiCalledFlag) {
            const storedIds = localStorage.getItem('balanceSheetAccessPageIds');
            if (storedIds) {
              try {
                const parsedIds = JSON.parse(storedIds);
                setAccessPageIds(parsedIds);
                setApiCalled(true);
              } catch (e) {
                setAccessPageIds([1]);
                setApiCalled(false);
              }
            } else {
              setAccessPageIds([1]);
              setApiCalled(false);
            }
          } else {
            setAccessPageIds([1]);
            setApiCalled(false);
          }
        }
      } catch (error) {
        console.error('Error parsing balanceSheetFormData:', error);
        setAccessPageIds([1]);
        setApiCalled(false);
      }
    } else {
      if (apiCalledFlag) {
        const storedIds = localStorage.getItem('balanceSheetAccessPageIds');
        if (storedIds) {
          try {
            const parsedIds = JSON.parse(storedIds);
            setAccessPageIds(parsedIds);
            setApiCalled(true);
          } catch (e) {
            setAccessPageIds([1]);
            setApiCalled(false);
          }
        } else {
          setAccessPageIds([1]);
          setApiCalled(false);
        }
      } else {
        setAccessPageIds([1]);
        setApiCalled(false);
      }
    }
  }, [balanceSheetValidation]);

  useEffect(() => {
    const handleRefreshHeader = () => {
      clearSavedPages(); 
      setAccessPageIds([1]);
      setApiCalled(false);
      localStorage.setItem('balanceSheetAccessPageIds', JSON.stringify([1]));
    };

    window.addEventListener('refreshBalanceSheetHeader', handleRefreshHeader);

    return () => {
      window.removeEventListener('refreshBalanceSheetHeader', handleRefreshHeader);
    };
  }, []);

  // Listen for validation updates from other components
  useEffect(() => {
    const handleValidationUpdate = (event) => {
      if (event.detail && Array.isArray(event.detail)) {
        setAccessPageIds(event.detail);
        setApiCalled(true);
        localStorage.setItem('balanceSheetApiCalled', 'true');
        localStorage.setItem('balanceSheetAccessPageIds', JSON.stringify(event.detail));
      }
    };

    window.addEventListener('balanceSheetValidationUpdated', handleValidationUpdate);
    return () => {
      window.removeEventListener('balanceSheetValidationUpdated', handleValidationUpdate);
    };
  }, []);

  const getPageNameFromRoute = (route) => {
    const routeMap = {
      '/balanceSheet': 'GeneralInformation',
      '/fixedAssets': 'FixedAssets',
      '/balanceSheetData': 'BalanceSheetData',
      '/profitAndLossAccount': 'ProfitAndLossAccount',
      '/employmentAndLabourCost': 'EmploymentAndLabourCost',
      '/input-items': 'InputItemsIndigenous',
      '/input-items-imported': 'InputItemsImported',
      '/products-and-by-products': 'ProductsAndByProducts',
      '/ict-usage-energy-conservation': 'ICTUsageAndEnergyConservation',
      // '/particulars-of-field-operations': 'ParticularsOfFieldOperations',
      '/part-a': 'PartA',
      '/part2': 'Part2',
      '/net-profit-tally': 'NetProfitTally',
      '/final-checklist': 'FinalCheckList',
      '/validation': 'Validation',
      '/report-selection': 'Reports',
    };
    return routeMap[route] || '';
  };

  const currentPageName = getPageNameFromRoute(location.pathname);
  const currentPageIndex = balanceSheetPages.findIndex(page => page.name === currentPageName);

  const statusUnitCode = localStorage.getItem('statusUnitCode');
  const isSkipBlocksStatus = ['4', '5', '7', '8'].includes(statusUnitCode);
  const effectiveAccessPageIds = (isSkipBlocksStatus && apiCalled) ? [1, 11, 12] : accessPageIds;
  const effectiveApiCalled = apiCalled;

  const blockAlertBalanceSheet = (item, index) => {
    if (['4', '5', '7', '8'].includes(statusUnitCode)) {
      toast.error('Sorry, you are not allowed to fill data in this Block', messages.COLOR_RED);
    } else {
      const previousStepNumber = index >= 1 ? index : 1; 
      toast.error(`Please Save Block ${previousStepNumber} Data`, messages.COLOR_RED);
    }
  };

  const processBar = (item, index) => {
    const isActive = currentPageName === item.name;
    const isAccessible = effectiveAccessPageIds.includes(item.id);

    let tempActive = false;
    if (effectiveApiCalled && effectiveAccessPageIds.length > 0) {
      const availablePageIds = balanceSheetPages
        .filter(page => effectiveAccessPageIds.includes(page.id))
        .map(page => page.id);
      const greenCount = Math.max(0, effectiveAccessPageIds.length - 1);
      const greenPageIds = availablePageIds.slice(0, greenCount);
      tempActive = greenPageIds.includes(item.id);
    }

    const isAccessibleForNavigation = effectiveApiCalled && isAccessible;

    return (
      isActive ? (
        <li key={item.id} className="current">
          <Link to={item.route}>
            <span>{index + 1}</span>
            <strong>{item.title}</strong>
          </Link>
        </li>
      ) : isAccessibleForNavigation ? (
        <li className={tempActive ? "active" : ""} key={item.id}>
          <Link to={item.route}>
            <span>{index + 1}</span>
            <strong>{item.title}</strong>
          </Link>
        </li>
      ) : (
        <li key={item.id}>
          <Link to="#" onClick={(e) => {
            e.preventDefault();
            const toastKey = `page-locked-${item.id}`;
            if (!toastShownRef.current[toastKey]) {
              blockAlertBalanceSheet(item, index);
              toastShownRef.current[toastKey] = true;
              setTimeout(() => delete toastShownRef.current[toastKey], 3000);
            }
          }}>
            <span>{index + 1}</span>
            <strong>{item.title}</strong>
          </Link>
        </li>
      )
    );
  };

  return (
    <div className="card mx-3 mb-3">
      <div className="card-body pb-1">
        <div className="row align-items-center">
          <ul id="progressbar" className="mb-3" style={{ width: '100%' }}>
            {balanceSheetPages.map((item, index) => processBar(item, index))}
          </ul>
        </div>
      </div>
      <style>
        {`
          #progressbar li {
            width: ${100 / balanceSheetPages.length}% !important;
          }
        `}
      </style>
    </div>
  );
}

